// src/components/IncomeKPI.tsx
import React, { useMemo } from 'react';
import { Row, Col } from 'antd';
import { DollarOutlined, CalendarOutlined, FieldTimeOutlined } from '@ant-design/icons';
import StatCard from './StatCard';
import { useNotifications } from '../hooks/useNotifications';
import { D, W, M, Y, currPrevSum, pct } from '../utils/kpi';

type Item = { total?: number; createdAt: string };

type Props = {
  /** Показать 5-ю карточку «За всё время» (по умолчанию — нет) */
  showAllTime?: boolean;
  /** (опц.) Отфильтрованный источник — для полной синхронизации с таблицей/донатом */
  source?: Item[];
};

const fmt = (n: number) => n.toLocaleString('ru-RU');

const IncomeKPI: React.FC<Props> = ({ showAllTime = false, source }) => {
  const hook = useNotifications(10_000); // берём побольше записей
  const items = source ?? hook.items;

  const data = useMemo(() => ({
    d: currPrevSum<Item>(items, D),
    w: currPrevSum<Item>(items, W),
    m: currPrevSum<Item>(items, M),
    y: currPrevSum<Item>(items, Y),
    all: {
      curr: items.reduce((s, n) => s + (n.total || 0), 0),
      prev: 0,
    },
  }), [items]);

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} md={12} lg={6}>
        <StatCard
          className="stat-card--kpi"
          accent
          icon={<FieldTimeOutlined />}
          title="Выручка (24 часа)"
          value={fmt(data.d.curr)}
          suffix="AMD"
          linkText="Отчёт"
          onLink={() => {}}
          deltaText={pct(data.d.curr, data.d.prev)}
          deltaHint="vs вчера"
        />
      </Col>

      <Col xs={24} md={12} lg={6}>
        <StatCard
          className="stat-card--kpi"
          accent
          icon={<CalendarOutlined />}
          title="Выручка (неделя)"
          value={fmt(data.w.curr)}
          suffix="AMD"
          linkText="Отчёт"
          onLink={() => {}}
          deltaText={pct(data.w.curr, data.w.prev)}
          deltaHint="vs прошл. неделя"
        />
      </Col>

      <Col xs={24} md={12} lg={6}>
        <StatCard
          className="stat-card--kpi"
          accent
          icon={<CalendarOutlined />}
          title="Выручка (месяц)"
          value={fmt(data.m.curr)}
          suffix="AMD"
          linkText="Отчёт"
          onLink={() => {}}
          deltaText={pct(data.m.curr, data.m.prev)}
          deltaHint="vs прошл. месяц"
        />
      </Col>

      <Col xs={24} md={12} lg={6}>
        <StatCard
          className="stat-card--kpi"
          accent
          icon={<CalendarOutlined />}
          title="Выручка (год)"
          value={fmt(data.y.curr)}
          suffix="AMD"
          linkText="Отчёт"
          onLink={() => {}}
          deltaText={pct(data.y.curr, data.y.prev)}
          deltaHint="vs прошл. год"
        />
      </Col>

      {showAllTime && (
        <Col xs={24} md={12} lg={6}>
          <StatCard
            className="stat-card--kpi"
            blueAccent
            icon={<DollarOutlined />}
            title="Выручка (за всё время)"
            value={fmt(data.all.curr)}
            suffix="AMD"
            linkText="Детализация"
            onLink={() => {}}
          />
        </Col>
      )}
    </Row>
  );
};

export default IncomeKPI;
